/**
 * Tracks whether event photos / videos were posted to each social platform.
 * Stored on bookings as `socialMediaShare`: { [platformId]: { photos, video } }.
 */

export const SOCIAL_PLATFORMS = [
  { id: 'facebook', label: 'Facebook', short: 'FB' },
  { id: 'instagram', label: 'Instagram', short: 'IG' },
  { id: 'tiktok', label: 'TikTok', short: 'TT' },
  { id: 'youtube', label: 'YouTube', short: 'YT' },
];

const PLATFORM_IDS = SOCIAL_PLATFORMS.map((p) => p.id);

export const DEFAULT_SOCIAL_MEDIA_SHARE = Object.freeze(
  Object.fromEntries(PLATFORM_IDS.map((id) => [id, { photos: false, video: false }]))
);

export function normalizeSocialMediaShare(raw) {
  const base = {};
  for (const id of PLATFORM_IDS) {
    base[id] = { photos: false, video: false };
  }
  if (!raw || typeof raw !== 'object') {
    return base;
  }
  for (const id of PLATFORM_IDS) {
    const entry = raw[id];
    if (entry && typeof entry === 'object') {
      base[id] = {
        photos: Boolean(entry.photos),
        video: Boolean(entry.video),
      };
    }
  }
  return base;
}

export function setSocialShareFlag(share, platformId, media, value) {
  const current = share[platformId] || { photos: false, video: false };
  return {
    ...share,
    [platformId]: {
      ...current,
      [media]: Boolean(value),
    },
  };
}

/** @param {'photo-only' | 'video-only' | 'combo'} serviceType */
export function countSocialShareProgress(share, serviceType) {
  const needsPhotos = serviceType !== 'video-only';
  const needsVideo = serviceType !== 'photo-only';
  let done = 0;
  let total = 0;
  for (const id of PLATFORM_IDS) {
    if (needsPhotos) {
      total += 1;
      if (share[id]?.photos) done += 1;
    }
    if (needsVideo) {
      total += 1;
      if (share[id]?.video) done += 1;
    }
  }
  return { done, total };
}

export function formatSocialShareSummary(share, serviceType) {
  const { done, total } = countSocialShareProgress(share, serviceType);
  if (total === 0) return ' - ';
  return `${done}/${total} posted`;
}

export function isSocialShareComplete(share, serviceType) {
  const { done, total } = countSocialShareProgress(share, serviceType);
  return total > 0 && done === total;
}

export function socialShareToExportColumns(share, serviceType) {
  const normalized = normalizeSocialMediaShare(share);
  const needsPhotos = serviceType !== 'video-only';
  const needsVideo = serviceType !== 'photo-only';
  const cols = {};
  for (const platform of SOCIAL_PLATFORMS) {
    const entry = normalized[platform.id];
    if (needsPhotos) {
      cols[`${platform.label} Photos`] = entry.photos ? 'Yes' : 'No';
    }
    if (needsVideo) {
      cols[`${platform.label} Video`] = entry.video ? 'Yes' : 'No';
    }
  }
  return cols;
}
